import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Shield, CheckCircle, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TermsAcceptanceModal = ({ isOpen, onAccept }) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Contenu des conditions (même que LegalPage)
  const legalContent = `
    <h1>Conditions d'utilisation, Politique de confidentialité et Politique de vie privée</h1>
    <p><strong>Dernière mise à jour :</strong> Janvier 2026</p>
    
    <p>Ce document constitue une entente légale entre l'utilisateur et <strong>SNE - Solutions Numérique en Enquêtes</strong> concernant l'utilisation de l'API.</p>
    
    <h2>1. Description du service</h2>
    <p>L'API permet d'effectuer des recherches vers des bases de données, registres, sites web et services tiers afin d'obtenir des résultats sous forme de liens, références ou métadonnées. Le Fournisseur n'héberge aucun contenu et n'en contrôle aucune source.</p>
    
    <h2>2. Abonnements et paiements</h2>
    <p>La facturation est assurée par <strong>Shopify</strong>. Toutes les conditions de paiement, taxes, renouvellements et politiques de facturation relèvent exclusivement de Shopify.</p>
    <p>Tous les paiements sont finaux et non remboursables.</p>
    
    <h2>3. Disponibilité</h2>
    <p>L'API est fournie telle quelle et selon la disponibilité. Aucune garantie de continuité, de performance ou d'exactitude n'est offerte.</p>
    
    <h2>4. Sources externes</h2>
    <p>Les résultats proviennent de sources externes indépendantes. Le Fournisseur ne garantit ni leur exactitude, ni leur légalité, ni leur disponibilité.</p>
    
    <h2>5. Limitation de responsabilité</h2>
    <p>Le Fournisseur ne pourra être tenu responsable des pertes financières, pertes de données ou dommages indirects résultant de l'utilisation de l'API.</p>
    
    <h2>6. Confidentialité et Vie privée</h2>
    <p>L'API traite uniquement les données nécessaires à son fonctionnement. Aucun résultat n'est stocké. Aucune donnée n'est vendue. Les informations de paiement sont traitées exclusivement par Shopify.</p>
    
    <h2>7. Propriété intellectuelle</h2>
    <p>L'ensemble de la plateforme SNE, incluant le concept, le code source, l'interface et les algorithmes, sont la propriété exclusive de SNE et sont protégés par les lois sur la propriété intellectuelle.</p>
    <p><strong>Il est strictement interdit</strong> de reproduire, copier, imiter ou procéder à l'ingénierie inverse de tout composant de l'API.</p>
    
    <h2>8. Responsabilité de l'utilisateur et conformité OSINT</h2>
    <p><strong>L'utilisateur est seul et entièrement responsable</strong> de l'utilisation qu'il fait des données obtenues via la plateforme.</p>
    <p>L'utilisateur s'engage à :</p>
    <ul>
      <li>Respecter toutes les lois applicables (LPRPDE, Loi 25 du Québec, vie privée)</li>
      <li>Utiliser les données uniquement à des fins légitimes</li>
      <li>Ne pas utiliser la plateforme pour du harcèlement ou de la surveillance illégale</li>
      <li>Obtenir les autorisations nécessaires lorsque requis par la loi</li>
    </ul>
    <p>SNE <strong>n'assume aucune responsabilité</strong> quant à l'usage que fait l'utilisateur des données récoltées et <strong>se dégage de toute responsabilité</strong> en cas de réclamation résultant de l'utilisation des données.</p>
    
    <h2>9. Indemnisation</h2>
    <p>L'utilisateur s'engage à <strong>indemniser et tenir indemne</strong> SNE contre toute réclamation découlant de son utilisation de la plateforme.</p>
    
    <h2>10. Résiliation</h2>
    <p>Le Fournisseur peut suspendre ou fermer un compte en cas d'abus ou de violation, sans remboursement.</p>
    
    <h2>11. Modifications</h2>
    <p>Le Fournisseur se réserve le droit de modifier ces conditions à tout moment. L'utilisateur sera informé des modifications importantes. L'utilisation continue de la plateforme après modification constitue une acceptation des nouvelles conditions.</p>
    
    <h2>12. Droit applicable</h2>
    <p>Ces conditions sont régies par les lois de la province de Québec et les lois fédérales du Canada. Tout litige relève des tribunaux du Québec.</p>
    
    <h2>13. Acceptation</h2>
    <p>En utilisant la plateforme SNE, l'utilisateur reconnaît avoir lu, compris et accepté l'ensemble de ces conditions, incluant les dispositions relatives à la propriété intellectuelle, à la protection de l'idée et à sa responsabilité personnelle quant à l'utilisation des données.</p>
  `;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolled(true);
    }
  };

  const handleAccept = async () => {
    if (!isChecked || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/auth/accept-terms`);
      setLoading(false);
      // Defensive: only call onAccept if it's actually a function
      if (typeof onAccept === 'function') {
        try {
          onAccept();
        } catch (cbErr) {
          console.error('onAccept callback error:', cbErr);
        }
      }
    } catch (err) {
      console.error('Accept terms error:', err);
      setLoading(false);
      if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-800 max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-amber-500" />
            <h2 className="text-2xl font-bold text-white">Conditions d&apos;utilisation</h2>
          </div>
          <p className="text-gray-400">
            Veuillez lire et accepter les conditions d&apos;utilisation pour continuer.
          </p>
        </div>

        {/* Scrollable Content */}
        <div 
          className="flex-1 overflow-y-scroll p-6"
          onScroll={handleScroll}
          style={{ minHeight: '300px' }}
        >
          <div 
            className="prose prose-invert prose-sm max-w-none terms-content"
            dangerouslySetInnerHTML={{ __html: legalContent }}
          />
          
          {/* End of document marker */}
          <div className="mt-6 p-4 bg-amber-900/20 border border-amber-900/30 rounded-lg">
            <div className="flex items-center gap-2 text-amber-400">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Fin du document</span>
            </div>
          </div>
        </div>

        {/* Footer with acceptance */}
        <div className="p-6 border-t border-gray-800 bg-gray-900/50">
          {!hasScrolled && (
            <div className="flex items-center gap-2 text-amber-400 mb-4 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>Veuillez faire défiler jusqu&apos;à la fin du document pour continuer.</span>
            </div>
          )}
          
          <div className="flex items-start gap-3 mb-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <Checkbox
              id="accept-terms"
              checked={isChecked}
              onCheckedChange={setIsChecked}
              disabled={!hasScrolled}
              className="mt-1 h-5 w-5 border-2 border-white data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
            />
            <label 
              htmlFor="accept-terms" 
              className={`text-sm cursor-pointer ${hasScrolled ? 'text-white' : 'text-gray-500'}`}
            >
              J&apos;ai lu et j&apos;accepte les conditions d&apos;utilisation, la politique de confidentialité 
              et la politique de vie privée. Je comprends que je suis seul responsable de l&apos;utilisation 
              des données obtenues via cette plateforme.
            </label>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <Button
            onClick={handleAccept}
            disabled={!isChecked || !hasScrolled || loading}
            className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            size="lg"
          >
            {loading ? (
              <>Chargement...</>
            ) : (
              <>
                <FileText className="w-5 h-5 mr-2" />
                Accepter et continuer
              </>
            )}
          </Button>
        </div>
      </div>

      <style>{`
        .terms-content h1 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #f59e0b;
          margin-bottom: 0.75rem;
        }
        .terms-content h2 {
          font-size: 1rem;
          font-weight: 600;
          color: #f5f5f5;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .terms-content p {
          color: #9ca3af;
          margin-bottom: 0.75rem;
          line-height: 1.6;
          font-size: 0.875rem;
        }
        .terms-content strong {
          color: #f59e0b;
        }
        .terms-content ul {
          color: #9ca3af;
          margin-left: 1.25rem;
          margin-bottom: 0.75rem;
          font-size: 0.875rem;
        }
        .terms-content li {
          margin-bottom: 0.25rem;
          list-style-type: disc;
        }
      `}</style>
    </div>
  );
};

export default TermsAcceptanceModal;
